import React, { useMemo } from 'react';
import type { FC } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';

import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { useGetNewsQuery } from '../../entities/news/api/newsApi';
import type { NewsArticle } from '../../entities/news/model/types';
import { formatNewsDate } from '../../entities/news/lib/format';
import styles from './NewsDetailPage.module.scss';
import { BackButton } from '../../components/ui/BackButton/BackButton';

interface NewsLocationState {
    article?: NewsArticle;
}

export const NewsDetailPage: FC = () => {
    const { newsId } = useParams<{ newsId: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const passed = (location.state as NewsLocationState | null)?.article;

    const { data, isLoading, isError } = useGetNewsQuery(
        { page_num: 1 },
        { skip: Boolean(passed) },
    );

    const article = passed ?? data?.data.find((item) => item.id === newsId) ?? null;

    const html = useMemo(
        () => (article ? DOMPurify.sanitize(article.description, { ADD_ATTR: ['target', 'rel'] }) : ''),
        [article],
    );

    const goToList = () => navigate('/news');

    if (!passed && isLoading) {
        return (
            <div className={styles.page}>
                <EmptyState title="Загружаем…" description="Секунду, открываем новость." />
            </div>
        );
    }

    if (isError || !article) {
        return (
            <div className={styles.page}>
                <EmptyState
                    title="Новость не найдена"
                    description="Возможно, её удалили или ссылка устарела."
                    action={{ label: 'Ко всем новостям', onClick: goToList }}
                />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <BackButton />
                <PageHeader
                    crumbs={[{ label: 'Новости', to: '/news' }, { label: article.title }]}
                    title={article.title}
                    meta={[formatNewsDate(article.created_at)]}
                />
            </div>

            <article className={styles.article}>
                <div className={styles.body} dangerouslySetInnerHTML={{ __html: html }} />
            </article>
        </div>
    );
};